import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Alert,
  Pagination,
  Tooltip,
} from "@mui/material";
import base64 from "base-64";
import { CircularLoading } from "@/components/common";
import { Coverage, OrganizationAffiliation } from "fhir/r5";
import { useState } from "react";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { GuideDetailsDialog } from "./GuideDetailsDialog";
import { useLocationContext } from "@/contexts/LocationContext";
import { Info, Send, SendAndArchive, Visibility } from "@mui/icons-material";
import dayjs from "dayjs";
import { GetCoverage } from "@/services/fhir/coverage/GetCoverage";
import { c } from "vite/dist/node/moduleRunnerTransport.d-DJ_mE5sf";
import { XmlDialog } from "./XmlDialog";
import { GetBinary } from "@/services/fhir/patientsummary/Binary/GetBinary";
import { useValueSet } from "@/hooks/useValueSet";
import { toast } from "react-toastify";

export interface GuideDetailsData {
  appointment: fhir5.Appointment;
  patient: fhir5.Patient | undefined;
  affiliation: OrganizationAffiliation | undefined;
  primaryOrganization: fhir5.Organization | undefined;
  participatingOrganization: fhir5.Organization | undefined;
  practitioner: fhir5.PractitionerRole | undefined;
  location: fhir5.Location | undefined;
  coverage: Coverage | undefined;
}

interface GuideEmissionTabProps {
  loading: boolean;
  error: Error | null;
  appointments: fhir5.Appointment[];
  patients: fhir5.Patient[];
  organizationAffiliation: OrganizationAffiliation[];
  primaryOrganizations: fhir5.Organization[];
  participatingOrganizations: fhir5.Organization[];
  documentReferences?: fhir5.DocumentReference[];
  filters: {
    organization: string;
    plan: string;
    procedure: string;
  };
  isEmittedGuides?: boolean;
  refetch?: () => void;
}

export function GuideEmissionTab({
  loading,
  error,
  appointments,
  patients,
  organizationAffiliation,
  primaryOrganizations,
  participatingOrganizations,
  documentReferences,
  refetch,
  filters,
  isEmittedGuides = false,
}: GuideEmissionTabProps) {
  const { practitionerRole } = usePractitionerRoleContext();
  const { options: ufOptions, loading: ufLoading } = useValueSet({
    valueSet: "UF",
    customUrl:
      "https://fluxmed.com.br/hapi/fhir/ValueSet/1.3.6.1.4.1.54413.1.1.6.11",
  });

  const { location: locations } = useLocationContext();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [xmlDialogOpen, setXmlDialogOpen] = useState(false);
  const [binaryXml, setBinaryXml] = useState("");
  const [selectedDetails, setSelectedDetails] =
    useState<GuideDetailsData | null>(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10; // Número de itens por página

  // Função para mudar de página
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  // Handler para abrir o dialog de detalhes
  const handleOpenDetails = async (appointment: fhir5.Appointment) => {
    const patient = patients.find(
      (p) =>
        p.id ===
        appointment.participant
          ?.find((part) => part.actor?.reference?.startsWith("Patient/"))
          ?.actor?.reference?.replace("Patient/", "")
    );

    const practitioner = practitionerRole?.find((pr) =>
      pr.resource?.id?.includes(
        appointment.participant
          ?.find((part) => part.actor?.reference?.startsWith("Practitioner/"))
          ?.actor?.reference?.replace("Practitioner/", "") || ""
      )
    )?.resource;

    const location = locations?.find((loc) =>
      loc.resource?.id?.includes(
        appointment.participant
          ?.find((part) => part.actor?.reference?.startsWith("Location/"))
          ?.actor?.reference?.replace("Location/", "") || ""
      )
    )?.resource;

    const insurancePlan = appointment.supportingInformation?.[0];
    const affiliation = organizationAffiliation.find(
      (aff) =>
        aff.id ===
        insurancePlan?.reference?.replace("OrganizationAffiliation/", "")
    );

    const coverages = await GetCoverage({
      patient: patient?.id,
    });

    const coverage = coverages?.entry?.find(
      (c: fhir5.BundleEntry<fhir5.Coverage>) =>
        c?.resource?.insurancePlan?.reference ===
        affiliation?.extension?.find(
          (e) =>
            e.url ===
            "https://fluxmed.com.br/fhir/StructureDefinition/insurance-plan-affiliation"
        )?.valueReference?.reference
    )?.resource;

    // Encontrar as organizações correspondentes
    const primaryOrganization = primaryOrganizations.find(
      (org) =>
        org.id ===
        affiliation?.organization?.reference?.replace("Organization/", "")
    );

    const participatingOrganization = participatingOrganizations.find(
      (org) =>
        org.id ===
        affiliation?.participatingOrganization?.reference?.replace(
          "Organization/",
          ""
        )
    );

    setSelectedDetails({
      appointment,
      patient,
      affiliation,
      primaryOrganization,
      participatingOrganization,
      practitioner,
      location,
      coverage,
    });
    setDetailsOpen(true);
  };

  const handleOpenXml = async (appointmentId: string) => {
    const docRef = documentReferences?.find(
      (doc: fhir5.DocumentReference) =>
        doc.context?.[0]?.reference === `Appointment/${appointmentId}`
    );

    if (docRef?.content?.[0]?.attachment?.url) {
      try {
        const response = await GetBinary({
          urlBinary: docRef.content[0].attachment.url,
        });

        setBinaryXml(base64.decode(response.data));
        setXmlDialogOpen(true);
      } catch (error) {
        toast.warning("Erro ao carregar guia");
      }
    }
  };

  // Handler para fechar o dialog
  const handleCloseDetails = () => {
    refetch?.();
    setDetailsOpen(false);
    setSelectedDetails(null);
  };

  if (loading) {
    return <CircularLoading />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Erro ao carregar consultas: {error.message}
      </Alert>
    );
  }

  if (!appointments.length) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "200px",
        }}
      >
        <Typography variant="h6" color="text.secondary">
          {isEmittedGuides
            ? "Nenhuma guia emitida"
            : "Nenhuma consulta pendente"}
        </Typography>
        <Typography color="text.secondary">
          {isEmittedGuides
            ? "Não há guias emitidas para exibir."
            : "Não há consultas realizadas aguardando emissão de guias."}
        </Typography>
      </Box>
    );
  }

  const filteredAppointments = appointments.filter((appointment) => {
    // Encontrar a afiliação correspondente
    const insurancePlan = appointment.supportingInformation?.[0];
    const affiliation = organizationAffiliation.find(
      (aff) =>
        aff.id ===
        insurancePlan?.reference?.replace("OrganizationAffiliation/", "")
    );

    // Verificar se a afiliação passa pelos filtros atuais
    const organizationName =
      affiliation?.participatingOrganization?.display?.toLowerCase() || "";
    const planName = insurancePlan?.display?.toLowerCase() || "";
    const procedures = (affiliation?.specialty || [])
      .map((specialty) => specialty.coding?.[0]?.display?.toLowerCase() || "")
      .join(" ");

    const normalizedFilters = {
      organization: filters.organization.toLowerCase().trim(),
      plan: filters.plan.toLowerCase().trim(),
      procedure: filters.procedure.toLowerCase().trim(),
    };

    return (
      (normalizedFilters.organization === "" ||
        organizationName.includes(normalizedFilters.organization)) &&
      (normalizedFilters.plan === "" ||
        planName.includes(normalizedFilters.plan)) &&
      (normalizedFilters.procedure === "" ||
        procedures.includes(normalizedFilters.procedure))
    );
  });

  // Cálculo dos itens que serão exibidos com base na página
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAppointments = filteredAppointments.slice(
    startIndex,
    endIndex
  );

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Paciente</TableCell>
              <TableCell>Profissional</TableCell>
              <TableCell>Procedimento</TableCell>
              {/* <TableCell>Especialidade</TableCell>
              <TableCell>Local</TableCell> */}
              <TableCell>Plano</TableCell>
              <TableCell>Operadora</TableCell>
              <TableCell>Data</TableCell>
              {/* <TableCell>Tipo</TableCell> */}
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAppointments.map((appointment) => {
              // Encontrar o paciente correspondente
              const patient = patients.find(
                (p) =>
                  p.id ===
                  appointment.participant
                    ?.find((part) =>
                      part.actor?.reference?.startsWith("Patient/")
                    )
                    ?.actor?.reference?.replace("Patient/", "")
              );

              // Encontrar informações dos participantes
              const practitioner = appointment.participant?.find((part) =>
                part.actor?.reference?.startsWith("Practitioner/")
              )?.actor;

              const healthcareService = appointment.participant?.find((part) =>
                part.actor?.reference?.startsWith("HealthcareService/")
              )?.actor;

              const location = appointment.participant?.find((part) =>
                part.actor?.reference?.startsWith("Location/")
              )?.actor;

              // Informações do plano e organização
              const insurancePlan = appointment.supportingInformation?.[0];
              const affiliation = organizationAffiliation.find(
                (aff) =>
                  aff.id ===
                  insurancePlan?.reference?.replace(
                    "OrganizationAffiliation/",
                    ""
                  )
              );

              // Tipo de consulta (presencial/virtual)
              const isVirtual = appointment?.virtualService?.length || 0 > 0;

              return (
                <TableRow hover key={appointment.id}>
                  <TableCell>
                    {patient?.name?.find((name) => name.use === "official")
                      ?.text || "Paciente não encontrado"}
                  </TableCell>
                  <TableCell>
                    {practitioner?.display || "Não especificado"}
                  </TableCell>
                  <TableCell>
                    {affiliation?.specialty
                      ?.map(
                        (specialty) =>
                          specialty.coding?.[0]?.display || "Não especificado"
                      )
                      .join(", ")}
                  </TableCell>
                  {/* <TableCell>
                  {healthcareService?.display || "Não especificado"}
                </TableCell>
                <TableCell>{location?.display || "Não especificado"}</TableCell> */}
                  <TableCell>
                    {insurancePlan?.display || "Não especificado"}
                  </TableCell>
                  <TableCell>
                    {affiliation?.participatingOrganization?.display ||
                      "Não especificado"}
                  </TableCell>
                  {/* <TableCell>
                  {isVirtual ? "Teleconsulta" : "Presencial"}
                </TableCell> */}
                  <TableCell>
                    {dayjs(appointment.start).format("DD/MM/YYYY")}
                  </TableCell>

                  {!isEmittedGuides ? (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={async () =>
                          await handleOpenDetails(appointment)
                        }
                      >
                        <Send fontSize="small" />
                      </IconButton>
                    </TableCell>
                  ) : (
                    <TableCell align="right">
                      <Tooltip title="Guia tiss emitida">
                        <IconButton
                          size="small"
                          onClick={async () =>
                            await handleOpenXml(appointment?.id || "")
                          }
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {/* Paginação */}
        {filteredAppointments.length > itemsPerPage && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <Pagination
              count={Math.ceil(filteredAppointments.length / itemsPerPage)}
              page={page}
              onChange={handleChangePage}
              color="primary"
              size="small"
            />
          </Box>
        )}

        {detailsOpen && (
          <GuideDetailsDialog
            open={detailsOpen}
            ufOptions={ufOptions}
            onClose={handleCloseDetails}
            details={selectedDetails}
          />
        )}

        {xmlDialogOpen && (
          <XmlDialog
            open={xmlDialogOpen}
            xml={binaryXml}
            onClose={() => {
              setXmlDialogOpen(false);
              setBinaryXml("");
            }}
          />
        )}
      </TableContainer>
    </Box>
  );
}
